// src/observer.ts
import equal from "fast-deep-equal";
import greenlet from "greenlet";
var Observer = class _Observer {
  #subscribers = [];
  #completed = false;
  #lastValue;
  get lastValue() {
    return this.#lastValue;
  }
  get completed() {
    return this.#completed;
  }
  subscribe(callback) {
    this.#subscribers.push(callback);
    return () => {
      this.unsubscribe(callback);
    };
  }
  subscribeWithInitial(callback) {
    this.#subscribers.push(callback);
    if (this.#lastValue !== void 0) callback(this.#lastValue);
    return () => {
      this.unsubscribe(callback);
    };
  }
  unsubscribe(callback) {
    this.#subscribers = this.#subscribers.filter((subscriber) => subscriber != callback);
  }
  complete() {
    if (!this.#completed) {
      this.#completed = true;
      this.#subscribers = [];
    }
  }
  notify(value) {
    if (!this.#completed) {
      this.#lastValue = value;
      this.#subscribers.forEach((subscriber) => subscriber(value));
    }
  }
  filter(filterFn) {
    let filteredObserver = new _Observer();
    this.subscribe((value) => {
      let filterValue = filterFn(value);
      if (filterValue) {
        filteredObserver.notify(value);
      }
    });
    return filteredObserver;
  }
  filterAsync(filterFn) {
    let filteredObserver = new _Observer();
    let wrappedFilterFn = typeof window == "undefined" ? filterFn : greenlet(filterFn);
    this.subscribe(async (value) => {
      if (await wrappedFilterFn(value)) {
        filteredObserver.notify(value);
      }
    });
    return filteredObserver;
  }
  map(mapFn) {
    let mappedObserver = new _Observer();
    this.subscribe((value) => {
      let mappedValue = mapFn(value);
      mappedObserver.notify(mappedValue);
    });
    return mappedObserver;
  }
  mapAsync(mapFn) {
    let mappedObserver = new _Observer();
    let wrappedMapFn = typeof window == "undefined" ? mapFn : greenlet(mapFn);
    this.subscribe(async (value) => {
      let mappedValue = await wrappedMapFn(value);
      mappedObserver.notify(mappedValue);
    });
    return mappedObserver;
  }
  once(callback) {
    let unsubscribe = this.subscribe((value) => {
      callback(value);
      unsubscribe();
    });
  }
  clear() {
    this.#subscribers = [];
  }
  merge(...observers) {
    let mergedObserver = new _Observer();
    for (let observer of [this, ...observers]) {
      observer.subscribe((value) => mergedObserver.notify(value));
    }
    return mergedObserver;
  }
  combine(observerA, observerB, observerC, observerD, observerE) {
    let combinedObserver = new _Observer();
    let observers = [this, observerA, observerB, observerC, observerD, observerE].filter(
      Boolean
    );
    combinedObserver.#lastValue = observers.map((observer) => observer.lastValue);
    for (let observer of observers) {
      observer.subscribe((value) => {
        let values = observers.map((observer2) => observer2.lastValue);
        combinedObserver.notify(values);
      });
    }
    return combinedObserver;
  }
  take(count) {
    let takenObserver = new _Observer();
    let currentCount = 0;
    let unsubscribe = this.subscribe((value) => {
      takenObserver.notify(value);
      currentCount++;
      if (currentCount >= count) {
        takenObserver.complete();
        unsubscribe();
      }
    });
    return takenObserver;
  }
  debounce(time, { maxWait } = {}) {
    let timerId;
    let lastCallTime = 0;
    let debouncedObserver = new _Observer();
    let clearTimer = () => {
      if (timerId) {
        clearTimeout(timerId);
        timerId = void 0;
      }
    };
    this.subscribe((value) => {
      let currentTime = Date.now();
      let timeSinceLastCall = currentTime - lastCallTime;
      lastCallTime = currentTime;
      clearTimer();
      if (maxWait && timeSinceLastCall >= maxWait) {
        debouncedObserver.notify(value);
      } else {
        timerId = setTimeout(() => {
          debouncedObserver.notify(value);
          clearTimer();
        }, time);
      }
    });
    return debouncedObserver;
  }
  throttle(time) {
    let lastNotificationTime = 0;
    let throttledObserver = new _Observer();
    this.subscribe((value) => {
      let currentTime = Date.now();
      if (currentTime - lastNotificationTime >= time) {
        throttledObserver.notify(value);
        lastNotificationTime = currentTime;
      }
    });
    return throttledObserver;
  }
  ignoreUntilChanged() {
    let previousValue;
    let distinctObserver = new _Observer();
    this.subscribe((value) => {
      if (value == previousValue || !equal(value, previousValue)) {
        distinctObserver.notify(value);
        previousValue = value;
      }
    });
    return distinctObserver;
  }
  static of = (...value) => {
    let observer = new _Observer();
    setTimeout(() => {
      value.forEach((v) => observer.notify(v));
    }, 0);
    return observer;
  };
  static merge(...observers) {
    return new _Observer().merge(...observers);
  }
};

// src/useObserver.ts
import { useEffect, useState } from "react";
var useObserver = (observer) => {
  let [state, setState] = useState(() => observer.lastValue);
  useEffect(() => {
    return observer.subscribe((value) => setState(value));
  }, [observer]);
  return state;
};
export {
  Observer,
  useObserver
};
//# sourceMappingURL=index.mjs.map